import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";
import { getSetupToken } from "@/domain/_shared/auth/services/auth.service";
import { Forced2FASetupPage } from "@/domain/two-factor-auth/components/forced-2fa-setup-page";
import { useGetUserEmailForSetup } from "@/domain/two-factor-auth/hooks/api/use-get-user-email-for-setup";
import { useUnsafeCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { getAccessToken } from "@/services/storage.service";

/**
 * Forced-2FA setup page. Serves two populations transparently:
 *  1. Fresh forced-setup login: only a 2fa_setup token, no access token. The
 *     email is fetched via the setup-token endpoint.
 *  2. Authenticated session redirected here by TwoFactorSetupGuard: the email
 *     comes from the current-user context.
 *
 * The underlying enrollment hooks pick their bearer internally, so the page
 * only branches on where the email comes from.
 */
export function Setup2FAPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("two-factor-auth");
  useDocumentTitle(t("forced.title"));
  // The setup-token flow renders this page outside ProtectedRoute, so the
  // CurrentUserContextProvider is not in scope. Use the unsafe variant so the
  // hook returns null instead of throwing in that branch.
  const currentUserContext = useUnsafeCurrentUserContext();
  const currentUser = currentUserContext?.currentUser ?? null;

  const isSetupTokenFlow = !!getSetupToken() && !getAccessToken();
  const setupTokenEmail = useGetUserEmailForSetup({ enabled: isSetupTokenFlow });

  useEffect(() => {
    if (!isSetupTokenFlow && !getAccessToken()) {
      navigate("/login");
      return;
    }
    if (isSetupTokenFlow && setupTokenEmail.isError) {
      navigate("/login");
    }
  }, [isSetupTokenFlow, setupTokenEmail.isError, navigate]);

  const isWaiting = isSetupTokenFlow ? setupTokenEmail.isLoading : !currentUser;
  if (isWaiting) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingDots color="#808080" />
      </div>
    );
  }

  const email = isSetupTokenFlow ? setupTokenEmail.data : currentUser?.email;
  const handleComplete = () => redirectAfterSetup(searchParams.get("redirect"));

  return <Forced2FASetupPage userEmail={email ?? ""} onComplete={handleComplete} />;
}

function redirectAfterSetup(redirect: string | null) {
  let validatedUrl: string;
  if (!redirect) {
    validatedUrl = `${window.location.origin}/`;
  } else {
    const decodedRedirect = decodeURIComponent(redirect);
    const isProtocolRelative = decodedRedirect.startsWith("//");
    if (isProtocolRelative) {
      validatedUrl = `${window.location.origin}/`;
    } else if (decodedRedirect.startsWith("/")) {
      validatedUrl = `${window.location.origin}${decodedRedirect}`;
    } else {
      try {
        const url = new URL(decodedRedirect);
        if (url.origin === window.location.origin) {
          validatedUrl = url.href;
        } else {
          validatedUrl = `${window.location.origin}/`;
        }
      } catch {
        validatedUrl = `${window.location.origin}/`;
      }
    }
  }
  window.location.replace(validatedUrl);
}
